import { Validations } from '../validations.js'
import Types from '@aeppic/types'
import Cache from '@aeppic/shared/cache'
import { assert } from '@aeppic/shared/assert'

import { isUiInterface, AeppicInterface, AeppicUiInterface } from '../aeppic.js'
import type { CommandAeppicInterface, ExecuteOptions } from './executor.js'
import { CommandExecutor } from './executor.js'
import { ConditionExecutor, ConditionOptions } from './conditions.js'
import { EditableDocument } from '../../model/index.js'
import { isDocument, isReference } from '../../model/is.js'
import type { ActionPackageLoader } from '../actions.js'
import { wrapInCachedQueryInterface } from '../cached-query-api.js'
import { commandCheck, commandReference, conditionCheck, DocumentCommandLookup } from './availability.js'
import type { CommandAvailability, CommandCheck, DocumentCommands } from './availability.js'

export type { ExecuteOptions }

const MAX_PARAMETER_JSON_LENGTH = 1 * 1024 * 1024
const MAX_PARAMETER_JSON_WARN_LENGTH = 0.5 * 1024 * 1024
const COMMAND_LOG_SIZE = 1000

const ALL_EXECUTION_RIGHT_ID = 'aa3ba2de-4b65-4152-ab1d-655e21de9efd'

type CommandResult = {
  ok: boolean
  value?: any
  error?: any
}

type CommandParameters = Record<string, unknown>

type AeppicDocumentLookup = {
  get(identifier: Types.IdOrReference): Promise<Types.Document | null | undefined>
}

type NewDocumentCommandUnavailableReason = {
  command: Types.Document | Types.IdOrReference
  available: false
  reason: string
}

type NewDocumentCommandResult = {
  ok: boolean
  source?: 'configured' | 'fallback'
  command?: Types.Document
  result?: CommandResult
  value?: unknown
  document?: Types.Document | EditableDocument | Types.Reference
  navigation?: unknown
  error?: unknown
  reasons?: NewDocumentCommandUnavailableReason[]
}

type CommandOptions = {
  serverExecutor?: Pick<ICommands, 'execute'>
  delayedWrites?: boolean
  packageLoader?: ActionPackageLoader
}

export class Commands implements ICommands {
  private _commandExecutor: CommandExecutor
  private _conditionExecutor: ConditionExecutor
  private _hasAccessCache = new Cache(100, 1000)

  constructor(private _aeppic: CommandAeppicInterface|AeppicUiInterface, private _options: CommandOptions = {}) {
    this._commandExecutor = new CommandExecutor({ Aeppic: _aeppic, delayedWrites: _options.delayedWrites === true, packageLoader: _options.packageLoader })
    this._conditionExecutor = new ConditionExecutor({ Aeppic: _aeppic })
    Object.freeze(this)
  }

  /**
   * Execute a command against `target` with `specifiedParameters` (available
   * to its actions as `context.params`).
   *
   * Commands marked `serverOnly` or with a run-as `account` are executed by the
   * server (`serverExecutor`); all others run in this runtime. Parameter forms
   * (`data.parametersForm`: defaults, calculations and validation of the
   * parameters, HTTP 422 `invalid_parameters` on errors) are applied by the
   * server's command execution only, so a command that relies on its
   * parameter form for untrusted input should be `serverOnly`.
   */
  async execute(commandIdentifier: Types.Document | Types.IdOrReference, target: Types.IdOrReference | EditableDocument, specifiedParameters: any, options: ExecuteOptions = {}) {
    const command = await this._getCommand(commandIdentifier)

    if (!command) {
      throw new Error('Unknown command or command not accessible')
    }

    const executeOptions = await this._resolveExecuteOptions(command, target, options)
    const { result } = await this._executeCommand(command, target, specifiedParameters, executeOptions)

    if (!executeOptions?.navigation && !executeOptions?.ui) {
      return result
    }

    if (!result?.ok) {
      return result
    }

    const returnedADocument = isReference(result.value) || isDocument(result.value)

    if (!returnedADocument) {
      return result
    }

    if (isUiInterface(this._aeppic)) {
      const commandNavigation = command.data.navigation

      switch (commandNavigation) {
        case 'drillInto':
          if (executeOptions.ui) {
            const dialogOptions = await this._getDialogOptions(command)
            this._aeppic.drillInto(result.value, { dialogOptions })
          } else {
            this._aeppic.drillInto(result.value)
          }
          break
        case 'navigate':
          this._aeppic.navigateTo(result.value)
          break
        case 'back':
          this._aeppic.navigateBack()
          break
        case 'dialog':
          if (executeOptions.ui) {
            // Command parameters configure the dialog design (e.g. which tab opens first).
            const dialogOptions = { ...await this._getDialogOptions(command), params: specifiedParameters || {} }
            this._aeppic.showDocumentDialog(result.value, dialogOptions).catch((error) => {
              console.error('Could not show command dialog', error)
            })
          }
          break
        case 'no':
        default:
      }
    }

    return result
  }

  async newDocument(formIdentifier: Types.IdOrReference, parent: Types.IdOrReference | EditableDocument, specifiedParameters: CommandParameters = {}, options: ExecuteOptions = {}): Promise<NewDocumentCommandResult> {
    const form = await this._getFormDocument(formIdentifier)

    if (!form) {
      return {
        ok: false,
        error: `Unknown form (${getId(formIdentifier) || JSON.stringify(formIdentifier)})`,
      }
    }

    const commandParameters = {
      ...(specifiedParameters || {}),
      formId: form.id,
    }
    const resolution = await this._resolveNewDocumentCommand(form, parent, commandParameters)

    if (!resolution.command) {
      return {
        ok: false,
        source: resolution.source,
        error: resolution.error,
        reasons: resolution.reasons,
      }
    }

    const executeOptions = {
      ...options,
      navigation: false,
      ui: false,
    }
    const { result } = await this._executeCommand(resolution.command, parent, commandParameters, executeOptions)

    if (!result?.ok) {
      return {
        ok: false,
        source: resolution.source,
        command: resolution.command,
        result,
        error: result?.error,
      }
    }

    this._applyInitialData(result.value, specifiedParameters?.initialData)

    const navigation = await this._handleNewDocumentNavigation(resolution.command, result, {
      ui: options.ui !== false,
    })

    return {
      ok: true,
      source: resolution.source,
      command: resolution.command,
      result,
      value: result.value,
      document: isDocument(result.value) || isReference(result.value) || EditableDocument.isEditableDocument(result.value) ? result.value : undefined,
      navigation,
    }
  }

  async _execute(commandIdentifier: Types.Document | Types.IdOrReference, target: Types.IdOrReference | EditableDocument, specifiedParameters: any, options: ExecuteOptions = {}): Promise<{ command: Types.Document, result: CommandResult }> {
    const command = await this._getCommand(commandIdentifier)

    if (!command) {
      throw new Error('Unknown command or command not accessible')
    }

    return this._executeCommand(command, target, specifiedParameters, options)
  }

  private async _executeCommand(command: Types.Document, target: Types.IdOrReference | EditableDocument, specifiedParameters: any, options: ExecuteOptions = {}): Promise<{ command: Types.Document, result: CommandResult }> {
    const commandParameters = specifiedParameters || {}

    verifyParameters(commandParameters)
    const clonedParameters = cloneDeep(commandParameters)

    let execution: Promise<CommandResult>

    if (runOnServer(command)) {
      if (!this._options.serverExecutor) {
        execution = this._commandExecutor.execute(command, target, clonedParameters, options)
      } else {
        execution = this._options.serverExecutor.execute(command, target, clonedParameters, options)
      }
    } else {
      execution = this._commandExecutor.execute(command, target, clonedParameters, options)
    }

    return { command, result: await execution }
  }

  private async _resolveExecuteOptions(command: Types.Document, target: Types.IdOrReference | EditableDocument, options: ExecuteOptions) {
    if (options.ui !== undefined) {
      return options
    }

    const ui = await this._resolveCommandsUiDefault(command, target)
    return { ...options, ui }
  }

  private async _resolveCommandsUiDefault(command: Types.Document, target: Types.IdOrReference | EditableDocument) {
    if (!isUiInterface(this._aeppic) || !this._isFeatureEnabled('commands-ui')) {
      return false
    }

    const featureOptions = this._getOptionsForFeature('commands-ui') as any
    const targetFormId = await this._getTargetFormId(target)

    return !matchesCommandsUiException(featureOptions, command.id, targetFormId)
  }

  private async _getTargetFormId(target: Types.IdOrReference | EditableDocument) {
    let targetDocument: Types.Document | EditableDocument

    if (EditableDocument.isEditableDocument(target) || isDocument(target)) {
      targetDocument = target
    } else {
      targetDocument = await this._aeppic.get(target)
    }

    return targetDocument?.f?.id ?? null
  }

  private async _getDialogOptions(command: Types.Document) {
    const featureOptions = this._getOptionsForFeature('commands-ui') as any
    const dialogOptions = featureOptions?.dialog ?? {}
    const dialog = await this._getDialogDocument(command.data.navigationTarget)
    const dialogSize = dialogOptions.dialogSize || getDialogSize(dialog)
    const useGenericNewDocumentDialog = shouldUseDefaultDialogCommands(dialog)

    return {
      ...dialogOptions,
      command,
      dialog,
      readonly: dialog?.data?.editable === false && !useGenericNewDocumentDialog ? true : dialogOptions.readonly,
      commands: useGenericNewDocumentDialog ? [] : firstArray(dialog?.data?.commands, dialogOptions.commands),
      dialogSize,
      designName: useGenericNewDocumentDialog ? 'edit' : firstNonEmpty(dialog?.data?.design, dialogOptions.designName),
      layoutId: firstIdOrString(command.data.layout, dialog?.data?.layout, dialogOptions.layoutId),
      preferredMode: firstNonEmpty(command.data.preferredMode, dialog?.data?.preferredMode, dialogOptions.preferredMode),
      title: useGenericNewDocumentDialog
        ? firstNonEmpty(command.data.title, dialog?.data?.title, command.data.label, command.data.name, dialog?.data?.name, dialogOptions.title, command.f?.text)
        : firstNonEmpty(command.data.title, dialog?.data?.title, dialogOptions.title, command.data.label, command.data.name, dialog?.data?.name, command.f?.text),
      subtitle: firstNonEmpty(command.data.subTitle, command.data.subtitle, dialog?.data?.subtitle, dialogOptions.subtitle),
    }
  }

  private async _getDialogDocument(dialogIdentifier: any) {
    const dialogId = getId(dialogIdentifier)

    if (!dialogId) {
      return null
    }

    try {
      const dialog = await this._aeppic.get(dialogIdentifier)
      return dialog?.f?.id === 'dialog-form' ? dialog : null
    } catch (error) {
      console.warn('Could not load command dialog configuration', dialogIdentifier, error)
      return null
    }
  }

  private async _getFormDocument(formIdentifier: Types.IdOrReference) {
    if (isDocument(formIdentifier)) {
      return formIdentifier.f?.id === 'form' ? formIdentifier : null
    }

    const formId = getId(formIdentifier)

    if (!formId) {
      return null
    }

    if (hasGetForm(this._aeppic)) {
      const aeppic = this._aeppic
      const form = await aeppic.getForm(formId)
      return form?.document || null
    }

    const document = await (this._aeppic as AeppicDocumentLookup).get(formIdentifier)
    return document?.f?.id === 'form' ? document : null
  }

  private async _resolveNewDocumentCommand(form: Types.Document, parent: Types.IdOrReference | EditableDocument, commandParameters: CommandParameters) {
    const configuredCommandRefs = Array.isArray(form?.data?.newCommands)
      ? form.data.newCommands
      : []

    if (configuredCommandRefs.length === 0) {
      return {
        source: 'fallback' as const,
        command: this._buildFallbackNewDocumentCommand(form),
        reasons: [],
      }
    }

    const reasons: NewDocumentCommandUnavailableReason[] = []

    for (const commandRef of configuredCommandRefs) {
      const command = await this._resolveDocumentDialogCommand(commandRef)

      if (!command || command.hidden === true) {
        reasons.push({ command: commandRef, available: false, reason: 'hidden-or-missing' })
        continue
      }

      const visible = await this.isVisible(command, parent, commandParameters)
      if (!visible) {
        reasons.push({ command, available: false, reason: 'not-visible' })
        continue
      }

      const enabled = await this.isEnabled(command, parent, commandParameters)
      if (!enabled) {
        reasons.push({ command, available: false, reason: await this.getDisabledReason(command, parent, commandParameters) || 'disabled' })
        continue
      }

      const hasRight = await this.hasRightToExecute(command, parent)
      if (!hasRight) {
        reasons.push({ command, available: false, reason: 'no-right' })
        continue
      }

      return {
        source: 'configured' as const,
        command,
        reasons,
      }
    }

    return {
      source: 'configured' as const,
      command: null,
      error: 'No configured new command is available',
      reasons,
    }
  }

  private _buildFallbackNewDocumentCommand(form: Types.Document): Types.Document {
    return {
      id: `system-fallback-new-document:${form.id}`,
      v: 'initial',
      p: 'system',
      a: [],
      a_depth: 0,
      a_forms: [],
      f: {
        id: 'command-form',
        v: 'initial',
        text: 'Command',
      },
      data: {
        name: 'new',
        label: 'Neu',
        form: {
          id: form.id,
          v: form.v,
          text: form.data?.name || form.f?.text || form.id,
        },
        document: emptyReference(),
        templateDocument: emptyReference(),
        navigation: 'dialog',
        navigationTarget: emptyReference(),
        save: false,
      },
      locks: [],
      inheritedLocks: [],
      created: {
        at: '',
        by: '',
        client: '',
        from: '',
      },
      modified: {
        at: '',
        by: '',
        client: '',
        from: '',
      },
    }
  }

  private async _handleNewDocumentNavigation(command: Types.Document, result: CommandResult, options: { ui: boolean }) {
    const returnedADocument = isReference(result.value) || isDocument(result.value)

    if (!returnedADocument || !isUiInterface(this._aeppic)) {
      return null
    }

    switch (command.data.navigation) {
      case 'drillInto':
        if (options.ui) {
          const dialogOptions = { ...await this._getDialogOptions(command), formView: 'new' }
          this._aeppic.drillInto(result.value, { dialogOptions })
          return { action: 'drillInto' }
        }
        this._aeppic.drillInto(result.value)
        return { action: 'drillInto' }
      case 'navigate':
        this._aeppic.navigateTo(result.value)
        return { action: 'navigate' }
      case 'back':
        this._aeppic.navigateBack()
        return { action: 'back' }
      case 'dialog':
        if (options.ui) {
          const dialogOptions = { ...await this._getDialogOptions(command), formView: 'new' }
          return this._aeppic.showDocumentDialog(result.value, dialogOptions)
        }
        return null
      case 'no':
      default:
        return null
    }
  }

  private _applyInitialData(value: unknown, initialData: unknown) {
    if (!EditableDocument.isEditableDocument(value) || !initialData || typeof initialData !== 'object' || Array.isArray(initialData)) {
      return
    }

    for (const [fieldName, fieldValue] of Object.entries(initialData as Record<string, unknown>)) {
      value.data[fieldName] = fieldValue
    }
  }

  _isFeatureEnabled(featureName: string) {
    return this._aeppic.Features?.isEnabled(featureName) ?? false
  }

  _getOptionsForFeature(featureName: string) {
    return this._aeppic.Features?.getOptionsFor(featureName) ?? {}
  }

  /** Resolve form commands, including groups, under this API's current identity. */
  async forDocument(targetIdentifier: Types.Document | Types.IdOrReference | EditableDocument, parameters: any = {}, options: ConditionOptions = {}): Promise<DocumentCommands> {
    const target = await this._getTarget(targetIdentifier)
    if (!target) {
      throw Object.assign(new Error('Command target not found'), { status: 404 })
    }

    // Conditions often issue identical queries. Share those reads only within this request.
    const api = wrapInCachedQueryInterface(this._aeppic)
    const commands = new Commands(api)
    const lookup = new DocumentCommandLookup(api, command => commands.getAvailability(command, target, parameters, options))
    return lookup.resolve(target)
  }

  async getAvailability(commandIdentifier: Types.Document | Types.IdOrReference, targetIdentifier: Types.Document | Types.IdOrReference | EditableDocument, parameters: any = {}, options: ConditionOptions = {}): Promise<CommandAvailability> {
    const [command, target] = await Promise.all([
      this._getCommand(commandIdentifier),
      this._getTarget(targetIdentifier),
    ])
    if (!command || !target) {
      throw new Error('Unknown command or target, or not accessible')
    }

    const isGroup = command.f.id === 'command-group-form'
    const [visibility, enabled, rights] = await Promise.all([
      this._checkSafely(() => this._checkVisibility(command, target, parameters, options)),
      this._checkSafely(() => this._checkEnabled(command, target, parameters, options)),
      this._checkSafely(async () => {
        const granted = isGroup || !!await this.hasRightToExecute(command, target)
        return commandCheck(granted, granted ? [] : ['No right to execute this command'])
      }),
    ])

    return {
      command: commandReference(command),
      kind: isGroup ? 'group' : 'command',
      visible: visibility.passed,
      enabled: enabled.passed,
      hasRight: rights.passed,
      available: visibility.passed && enabled.passed && rights.passed,
      checks: { visibility, enabled, rights },
    }
  }

  private async _checkSafely(check: () => Promise<CommandCheck>): Promise<CommandCheck> {
    try {
      return await check()
    } catch {
      // Internal errors can contain query data; only authored explanations are public.
      return commandCheck(false, ['Command availability could not be evaluated'])
    }
  }

  private _getTarget(target: Types.Document | Types.IdOrReference | EditableDocument) {
    if (isDocument(target) || EditableDocument.isEditableDocument(target)) {
      return target
    }
    return this._aeppic.get(target)
  }

  async hasRightToExecute(commandIdentifier: Types.Document | Types.IdOrReference, targetIdentifier: Types.Document | Types.IdOrReference | EditableDocument) {
    const command = await this._getCommand(commandIdentifier)

    if (!command) {
      throw new Error('Could not locate command')
    }

    const target = await this._getTarget(targetIdentifier)

    if (!target) {
      throw new Error('Could not locate target')
    }

    if (!this._isFeatureEnabled('commands-rights-verification')) {
      return true
    }

    const hasAllCommandExecutionRights = await this._hasAccessCache.lookup(`r:${ALL_EXECUTION_RIGHT_ID}:${target.id}`, async () => {
      if (this._isFeatureEnabled('commands-rights-verification-ignore-allExecutionRights')) {
        const options = this._getOptionsForFeature('commands-rights-verification-ignore-allExecutionRights') as { accounts?: string[] }

        const account = 'Account' in this._aeppic ? this._aeppic.Account as Types.Document : undefined
        if (account && options?.accounts?.includes(account.id)) {
          return false
        }
      }

      return this._aeppic.hasRight(target, ALL_EXECUTION_RIGHT_ID)
    })

    if (hasAllCommandExecutionRights) {
      return true
    }

    return this._hasAccessCache.lookup(`c:${command.id}:${target.id}`, async () => {
      const queryForRights = this._aeppic.Query.global()
        .form('command-execution-right-form').where('data.allowAllCommands').is(true)
        .or.form('command-execution-right-form').where('data.allowedCommands').is(command.id)
      const rightsGrantingAccessToCommand = await this._aeppic.find(queryForRights)
      const rightsAlsoMatchingTarget = rightsGrantingAccessToCommand.filter(right => doesRightApplyToTarget(right, target))

      if (!rightsAlsoMatchingTarget.length) {
        return false
      }

      return this._aeppic.hasRight(target, rightsAlsoMatchingTarget)
    })
  }

  async isVisible(commandIdentifier: Types.Document | Types.IdOrReference, targetIdentifier: Types.IdOrReference | EditableDocument, parameters: any, options: ConditionOptions = {}) {
    const [command, target] = await this._resolveCheckInputs(commandIdentifier, targetIdentifier)
    const result = await this._checkVisibility(command, target, parameters, options)
    return result.passed
  }

  async isEnabled(commandIdentifier: Types.Document | Types.IdOrReference, targetIdentifier: Types.IdOrReference | EditableDocument, parameters: any, options: ConditionOptions = {}) {
    const [command, target] = await this._resolveCheckInputs(commandIdentifier, targetIdentifier)
    const result = await this._checkEnabled(command, target, parameters, options)
    return result.passed
  }

  async getDisabledReason(commandIdentifier: Types.Document | Types.IdOrReference, targetIdentifier: Types.IdOrReference | EditableDocument, parameters?: any, options: ConditionOptions = {}): Promise<string> {
    const [command, target] = await this._resolveCheckInputs(commandIdentifier, targetIdentifier)
    const result = await this._checkEnabled(command, target, parameters, options)
    return result.reasons.join('; ')
  }

  private async _resolveCheckInputs(commandIdentifier: Types.Document | Types.IdOrReference, targetIdentifier: Types.IdOrReference | EditableDocument) {
    const [command, target] = await Promise.all([this._getCommand(commandIdentifier), this._getTarget(targetIdentifier)])
    if (!command || !target) {
      throw new Error('Unknown command or target, or not accessible')
    }
    return [command, target] as const
  }

  private async _checkVisibility(command: Types.Document, target: Types.Document | EditableDocument, parameters: any, options: ConditionOptions): Promise<CommandCheck> {
    if (command.hidden === true) {
      return commandCheck(false, ['Command is hidden'])
    }
    if (command.data.hideOnReadonly === true && target.readonly === true) {
      return commandCheck(false, ['Document is read-only'])
    }

    return this._checkConditions(command, 'visibleConditions', target, parameters, options)
  }

  private async _checkEnabled(command: Types.Document, target: Types.Document | EditableDocument, parameters: any, options: ConditionOptions): Promise<CommandCheck> {
    if (hasCommandActions(command) && command.data.skipValidation !== true) {
      const validation = await new Validations(this._aeppic as any).checkTargetValidationRequirements(target, 'COMMAND_TARGET', command.data)
      if (!validation.passed) {
        const reasons = validation.errors.length ? validation.errors : validation.checks
          .filter(check => check.status !== 'PASSED')
          .map(check => `${check.rule?.data?.label || check.rule?.data?.name || check.key}: ${check.status}`)
        return commandCheck(false, reasons)
      }
    }

    return this._checkConditions(command, 'enabledConditions', target, parameters, options)
  }

  private async _checkConditions(command: Types.Document, fieldName: string, target: Types.Document | EditableDocument, parameters: any, options: ConditionOptions): Promise<CommandCheck> {
    const conditions = await this._getConditions(command, fieldName)
    verifyParameters(parameters || {})
    const clonedParameters = cloneDeep(parameters || {})
    const results = await this._conditionExecutor.evaluate(conditions, target, clonedParameters, options)
    return conditionCheck(results)
  }

  private async _describeValidationErrors(targetDoc: Types.Document | EditableDocument): Promise<string> {
    try {
      const form = await (this._aeppic as any).getFormForDocument(targetDoc)
      if (!form?.summarizeValidationErrors) return 'Unvollständig'
      const details = form.summarizeValidationErrors(targetDoc.data)
      return details ? `Unvollständig: ${details}` : 'Unvollständig'
    } catch {
      return 'Unvollständig'
    }
  }

  private _getCommand(commandIdentifier: Types.Document | Types.IdOrReference) {
    if (isDocument(commandIdentifier)) {
      if (EditableDocument.isEditableDocument(commandIdentifier)) {
        return commandIdentifier.cloneAsDocument()
      } else {
        return commandIdentifier
      }
    } else {
      return this._aeppic.get(commandIdentifier)
    }
  }

  private async _resolveDocumentDialogCommand(commandRef: Types.Document | Types.IdOrReference) {
    if (!commandRef) {
      return null
    }

    if (EditableDocument.isEditableDocument(commandRef)) {
      return commandRef.cloneAsDocument()
    }

    if (isDocument(commandRef)) {
      return commandRef
    }

    try {
      return await this._aeppic.get(commandRef)
    } catch (error) {
      console.warn('Could not load command', commandRef, error)
      return null
    }
  }

  private async _getConditions(command, fieldName) {
    if (!(fieldName in command.data)) {
      return []
    }

    if (!(<Types.ReferenceField[]>command.data[fieldName]).length) {
      return []
    }

    const conditions = await this._aeppic.getAll(<Types.ReferenceField[]>command.data[fieldName])
    if (conditions.length !== command.data[fieldName].length || conditions.some(condition => !condition)) {
      throw new Error('Condition not found or not accessible')
    }
    return conditions
  }
}

export type ICommands = Pick<Commands, 'execute' | 'newDocument' | 'forDocument' | 'getAvailability'>

function emptyReference() {
  return {
    id: '',
    v: '',
    text: '',
  }
}

function hasGetForm(aeppic: CommandAeppicInterface | AeppicUiInterface): aeppic is (CommandAeppicInterface | AeppicUiInterface) & { getForm(formId: string): Promise<{ document?: Types.Document } | undefined> } {
  return 'getForm' in aeppic && typeof aeppic.getForm === 'function'
}

function runOnServer(command: Types.Document) {
  return command.data.serverOnly || (command.data.account && (<Types.Reference>command.data.account).id)
}

function verifyParameters(parameters: any) {
  for (const name of Object.keys(parameters)) {
    const value = parameters[name]

    if (EditableDocument.isEditableDocument(value)) {
      throw new Error('Cannot pass an EditableDocument as parameters. Use cloneAsDocument() to clone EditableDocuments first.')
    }
  }
}

function cloneDeep(obj) {
  const json = JSON.stringify(obj)

  if (json.length > MAX_PARAMETER_JSON_WARN_LENGTH) {
    console.warn(`Command parameters when stringified should not exceed #${MAX_PARAMETER_JSON_WARN_LENGTH} bytes and will cause an exception at #${MAX_PARAMETER_JSON_LENGTH}. These are #${json.length} bytes big.`)
  }

  if (json.length > MAX_PARAMETER_JSON_LENGTH) {
    throw new Error(`Command parameters exceed stringified maximum size (${json.length} > ${MAX_PARAMETER_JSON_LENGTH})`)
  }

  return JSON.parse(json)
}

function doesRightApplyToTarget(right: Types.Document, target: Types.Document) {
  assert(right?.f.id === 'command-execution-right-form')

  if (right.data.applyToAllForms) {
    return true
  }

  const targetFormsRightIsLimitedTo: Types.ReferenceField[] = right.data.targetForms as Types.ReferenceField[] ?? []

  if (targetFormsRightIsLimitedTo.some(ref => ref.id === target.f.id)) {
    return true
  }

  return false
}

function hasCommandActions(command: Types.Document): boolean {
  if (command.data.actions && (<Types.Reference[]>command.data.actions).length > 0) {
    return true
  }

  if (command.data.action && (<Types.Reference>command.data.action).id) {
    return true
  }

  return false
}

function doesRightApplyToCommand(right: Types.Document, command: Types.Document) {
  assert(right?.f.id === 'command-execution-right-form')

  if (right.data.allowAllCommands) {
    return true
  }

  const commandsRightIsLimitedTo: Types.ReferenceField[] = right.data.allowedCommands as Types.ReferenceField[] ?? []

  if (commandsRightIsLimitedTo.some(ref => ref.id === command.id)) {
    return true
  }

  return false
}

function matchesCommandsUiException(options: any, commandId: string, targetFormId: string) {
  const exceptions = options?.exceptions ?? options?.except ?? {}

  return includesId(exceptions.commandIds ?? exceptions.commands, commandId) ||
         includesId(exceptions.targetFormIds ?? exceptions.formIds ?? exceptions.forms, targetFormId)
}

function includesId(values: any, id: string) {
  if (!id || !Array.isArray(values)) {
    return false
  }

  return values.some(value => getId(value) === id)
}

function getId(value: any) {
  if (!value) {
    return null
  }

  if (typeof value === 'string') {
    return value || null
  }

  return value.id || null
}

function firstNonEmpty(...values: any[]) {
  return values.find(value => typeof value === 'string' && value.trim().length > 0) || ''
}

function firstIdOrString(...values: any[]) {
  for (const value of values) {
    const id = getId(value)

    if (id) {
      return id
    }
  }

  return ''
}

function firstArray(...values: any[]) {
  return values.find(value => Array.isArray(value) && value.length > 0) || []
}

function getDialogSize(dialog: Types.Document) {
  const width = firstNonEmpty(dialog?.data?.dialogWidth)
  const height = firstNonEmpty(dialog?.data?.dialogHeight)

  if (!width && !height) {
    return undefined
  }

  return {
    ...(width ? { width } : {}),
    ...(height ? { height } : {}),
  }
}

function shouldUseDefaultDialogCommands(dialog: Types.Document) {
  return typeof dialog?.data?.design === 'string'
    && dialog.data.design.split(',').map(part => part.trim()).includes('generic-new-document-dialog')
}
